import { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Fieldset,
  FieldsetLegend,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import {
  WorkflowSelector,
  StageSelector,
} from "#app/components/entity-selectors";
import {
  PageHeader,
  PageHeaderTitle,
  SETTINGS_CONTENT_MAX_WIDTH,
  SettingsBackBar,
  SettingsBackBarClearance,
  SettingsPageGutter,
} from "#app/components/page-header";
import { SettingsGroup, SettingsGroupContent } from "./settings-group";
import {
  HTTP_METHODS,
  WEBHOOK_SOURCES,
  describeWebhook,
  optionLabel,
  pinnedStageId,
  triggerNeedsStage,
  webhookSource,
  webhookTargetNames,
  webhookUrlProblem,
} from "./webhook-options";
import { type WebhookItem } from "#app/hooks/settings/use-webhook-mutations";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { CreateWebhookInput } from "#app/lib/data";
import {
  PICKER_CONTROL_SELECTOR,
  focusFirstInvalid,
} from "#app/lib/utils/focus-first-invalid";

type WebhookFormViewProps = {
  webhook: WebhookItem | null;
  onCancel: () => void;
  onSubmit: (data: CreateWebhookInput) => Promise<void>;
};

/** The `when` JSON column as this form writes it: an optional stage pin. */
type WebhookWhen = { stageId?: string };

export function WebhookFormView({ webhook, onCancel, onSubmit }: WebhookFormViewProps) {
  const isMobile = useIsMobile();
  const isEditing = !!webhook;
  const { stageMap, workflowMap, isHydrated } = useWorkspaceData();

  const [name, setName] = useState(webhook?.name ?? "");
  const [url, setUrl] = useState(webhook?.url ?? "");
  // The URL rule speaks after the first blur, so it does not flash while
  // the user is still typing "https://".
  const [urlTouched, setUrlTouched] = useState(isEditing);
  const [method, setMethod] = useState(webhook?.method ?? "post");
  const [resource, setResource] = useState(webhook?.resource ?? "issues");
  const [trigger, setTrigger] = useState(
    webhook?.trigger ?? webhookSource(webhook?.resource).triggers[0]!.value,
  );
  const [stageId, setStageId] = useState<string | null>(() => pinnedStageId(webhook?.when));
  // The workflow picker follows the pinned stage until the user picks one.
  // It is derived, not seeded once: the workspace maps can load after the
  // form mounts, and a seed taken then would stay empty.
  const [pickedWorkflowId, setPickedWorkflowId] = useState<string | null | undefined>(undefined);
  const workflowId =
    pickedWorkflowId !== undefined
      ? pickedWorkflowId
      : stageId
        ? (stageMap.get(stageId)?.workflowId ?? null)
        : null;
  const [joins, setJoins] = useState<Record<string, boolean>>(
    (webhook?.joins as Record<string, boolean>) ?? {},
  );
  const [submitting, setSubmitting] = useState(false);
  // Errors show only after a blocked press, then clear as answers arrive.
  const [attempted, setAttempted] = useState(false);

  const nameFieldRef = useRef<HTMLDivElement>(null);
  const urlFieldRef = useRef<HTMLDivElement>(null);
  const targetFieldRef = useRef<HTMLDivElement>(null);

  const source = webhookSource(resource);
  const showTarget = triggerNeedsStage(resource, trigger);
  // The live restatement of the pick, in plain words. Target names come from
  // the workspace maps the pickers already draw from.
  const sentence = describeWebhook(
    resource,
    trigger,
    webhookTargetNames(stageId, stageMap, workflowMap),
  );

  const nameMissing = name.trim() === "";
  const urlMissing = url.trim() === "";
  // The server's https rule, said before the save instead of after it.
  const urlProblem = webhookUrlProblem(url);
  // A pin the workspace does not hold: the stage was deleted after the save.
  // The column has no foreign key, so the form must not send it again.
  const stageGone = !!stageId && isHydrated && !stageMap.has(stageId);
  const targetMissing = showTarget && (!stageId || stageGone);
  const nameError = attempted && nameMissing;
  const urlError = (attempted && urlMissing) || ((attempted || urlTouched) && !!urlProblem);
  const targetError = (attempted && targetMissing) || (showTarget && stageGone);

  function handleSourceChange(next: string) {
    setResource(next);
    // Triggers and joins belong to the source: start the new one clean.
    setTrigger(webhookSource(next).triggers[0]!.value);
    setStageId(null);
    setPickedWorkflowId(null);
    setJoins({});
  }

  function handleTriggerChange(next: string) {
    setTrigger(next);
    if (!triggerNeedsStage(resource, next)) {
      setStageId(null);
      setPickedWorkflowId(null);
    }
  }

  function handleWorkflowChange(ids: string[]) {
    const id = ids[0] ?? null;
    setPickedWorkflowId(id);
    setStageId(null);
  }

  function handleJoinToggle(key: string) {
    setJoins((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleSubmit() {
    if (submitting) return;
    if (nameMissing || urlMissing || urlProblem || targetMissing) {
      setAttempted(true);
      focusFirstInvalid([
        { root: nameFieldRef.current, selector: "input", invalid: nameMissing },
        // The URL input itself: the Method select before it keeps a hidden
        // input of its own, and a bare "input" would land focus on Method.
        { root: urlFieldRef.current, selector: 'input[type="url"]', invalid: urlMissing || !!urlProblem },
        { root: targetFieldRef.current, selector: PICKER_CONTROL_SELECTOR, invalid: targetMissing },
      ]);
      return;
    }
    setSubmitting(true);
    try {
      const when: WebhookWhen = {};
      if (showTarget && stageId) when.stageId = stageId;

      const activeJoins: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(joins)) {
        if (v) activeJoins[k] = true;
      }

      await onSubmit({
        name: name.trim(),
        url: url.trim(),
        method,
        resource,
        trigger,
        when,
        joins: activeJoins,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar label="API" onClick={onCancel} />
      <ScrollArea>
        <SettingsBackBarClearance />
        <PageHeader centered pinnedBack>
          <PageHeaderTitle>{isEditing ? "Edit webhook" : "Create webhook"}</PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box
            as="form"
            gap="2xl"
            fill="width"
            maxWidth={SETTINGS_CONTENT_MAX_WIDTH}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            {/* The two groups mirror the detail view's, so what you fill in
              * here is what you read back there. */}
            <SettingsGroup label="Endpoint">
              <SettingsGroupContent>
                <Box gap="md">
                  <Field invalid={nameError} ref={nameFieldRef}>
                    <FieldLabel required>Name</FieldLabel>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Lead router"
                      autoFocus={!isMobile}
                    />
                    {nameError ? <FieldError match>Give the webhook a name.</FieldError> : null}
                  </Field>

                  <Field invalid={urlError} ref={urlFieldRef}>
                    <FieldLabel required>URL</FieldLabel>
                    <Box direction={isMobile ? "column" : "row"} gap="sm" fill="width">
                      <Select
                        value={method}
                        onValueChange={(next: string | null) => {
                          if (next) setMethod(next);
                        }}
                      >
                        <SelectTrigger width={isMobile ? "full" : "auto"} aria-label="Method">
                          <SelectValue placeholder="Method">
                            {(value: string) => optionLabel(HTTP_METHODS, value, "Method")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectPopup>
                          {HTTP_METHODS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectPopup>
                      </Select>
                      <Box flex={1} minWidth={0}>
                        <Input
                          type="url"
                          inputMode="url"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          onBlur={() => setUrlTouched(true)}
                          placeholder="https://..."
                        />
                      </Box>
                    </Box>
                    {urlError ? (
                      <FieldError match>
                        {urlMissing ? "Enter the URL to send requests to." : urlProblem}
                      </FieldError>
                    ) : null}
                  </Field>
                </Box>
              </SettingsGroupContent>
            </SettingsGroup>

            <SettingsGroup label="Event">
              <SettingsGroupContent>
                <Box gap="md">
                  {/* One source is not a choice: the trigger labels already
                    * name it, so the picker waits for a second source. */}
                  {WEBHOOK_SOURCES.length > 1 ? (
                    <Field>
                      <FieldLabel>Source</FieldLabel>
                      <FieldDescription>The record whose changes send a request.</FieldDescription>
                      <Select
                        value={resource}
                        onValueChange={(next: string | null) => {
                          if (next) handleSourceChange(next);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select...">
                            {(value: string) => optionLabel(WEBHOOK_SOURCES, value, "Select...")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectPopup>
                          {WEBHOOK_SOURCES.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectPopup>
                      </Select>
                    </Field>
                  ) : null}

                  <Field>
                    <FieldLabel>Trigger</FieldLabel>
                    <Select
                      value={trigger}
                      onValueChange={(next: string | null) => {
                        if (next) handleTriggerChange(next);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select...">
                          {(value: string) => optionLabel(source.triggers, value, "Select...")}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectPopup>
                        {source.triggers.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectPopup>
                    </Select>
                    {sentence ? <FieldDescription>{sentence}</FieldDescription> : null}
                  </Field>

                  {showTarget ? (
                    <Field invalid={targetError} ref={targetFieldRef}>
                      <FieldLabel required>Target</FieldLabel>
                      <Box direction={isMobile ? "column" : "row"} gap="sm" fill="width">
                        <Box flex={1} minWidth={0}>
                          {/* The concrete selectors ship their own trigger and
                            * content children; `contentFit` sizes the popup to
                            * the trigger. */}
                          <WorkflowSelector
                            value={workflowId}
                            onValueChange={handleWorkflowChange}
                            placeholder="Workflow"
                            variant="popover"
                            contentFit
                            field
                            fill
                          />
                        </Box>
                        {workflowId ? (
                          <Box flex={1} minWidth={0}>
                            <StageSelector
                              workflowId={workflowId}
                              value={stageId}
                              onValueChange={setStageId}
                              placeholder="Stage"
                              variant="popover"
                              field
                              fill
                            />
                          </Box>
                        ) : null}
                      </Box>
                      {targetError ? (
                        <FieldError match>
                          {stageGone
                            ? "This stage no longer exists. Pick another stage."
                            : "Pick the stage that fires this webhook."}
                        </FieldError>
                      ) : null}
                    </Field>
                  ) : null}

                  {/* A Fieldset, so each checkbox keeps its own name. */}
                  <Fieldset>
                    <FieldsetLegend>Includes</FieldsetLegend>
                    <Text size="xs" variant="muted">
                      Related records added to the request body.
                    </Text>
                    <Box gap="sm" paddingTop="xs">
                      {source.joins.map((join) => (
                        <Label key={join.key}>
                          <Checkbox
                            checked={joins[join.key] ?? false}
                            onCheckedChange={() => handleJoinToggle(join.key)}
                          />
                          <Text size="sm" weight="normal">
                            {join.label}
                          </Text>
                        </Label>
                      ))}
                    </Box>
                  </Fieldset>
                </Box>
              </SettingsGroupContent>
            </SettingsGroup>

            {/* Live submit: a blocked press marks what is missing and lands
              * focus on it, instead of a disabled button that cannot say. */}
            <Box direction="row" gap="sm" justify="end">
              <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {isEditing ? "Save changes" : "Create webhook"}
              </Button>
            </Box>

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
